/* global localStorage */
import firebase from 'firebase'
import base64url from 'base64-url'
import * as request from '../request'
import { equals, either, split, forEach, pipe } from 'ramda'

const twitterProvider = new firebase.auth.TwitterAuthProvider()
const facebookProvider = new firebase.auth.FacebookAuthProvider()

const AuthService = {
  /**
   * Signin with twitter (popup)
   *
   * @returns { accessToken, accessSecret }
   */
  async twitterSignin () {
    const { credential } = await firebase
      .auth()
      .signInWithPopup(twitterProvider)

    if (!credential) {
      throw new Error('Error signing in with Twitter')
    }

    const { accessToken, secret: accessSecret } = credential
    return { accessToken, accessSecret }
  },

  /**
   * Signin with Facebook (popup)
   *
   * @returns {Promise<Object>}
   */
  async facebookOAuth () {
    const addScopesToProvider = pipe(
      split(','),
      forEach(scope => facebookProvider.addScope(scope))
    )
    addScopesToProvider(process.env.REACT_APP_FACEBOOK_API_SCOPES)

    const response = await firebase.auth().signInWithPopup(facebookProvider)
    const { credential, additionalUserInfo } = response

    if (!credential || !additionalUserInfo) {
      throw new Error('Error signing in with Facebook')
    }

    const { accessToken } = credential
    const { id: userId } = additionalUserInfo.profile
    return { accessToken, userId }
  },

  /**
   * Send the accessToken and accessSecret to the bluerobot backend
   * to signin to bluerobot
   * @param  {string} options.accessToken  user's Twitter access token
   * @param  {string} options.accessSecret user's Twitter access secret
   */
  async bluerobotSignin ({ accessToken, accessSecret }) {
    const response = await request.unAuthorized('/signin', {
      method: 'POST',
      body: {
        accessToken,
        accessSecret
      }
    })

    const { token } = await response.json()
    return token
  },

  /**
   * Send the accessToken and user id to the bluerobot backend
   * to signin to bluerobot
   * @param  {object} options
   * @param  {string} options.accessToken  user's Facebook access token
   * @param  {string} options.userId user's Facebook user ID
   * @returns {Promise<string>} The Blue Robot JWT Token
   */

  async bluerobotFbSignin ({ accessToken, userId }) {
    const response = await request.unAuthorized('/fb-signin', {
      method: 'POST',
      body: {
        accessToken,
        userId
      }
    })

    const { token } = await response.json()
    return token
  },

  /**
   * Send the accessToken and user id to the bluerobot backend
   * to signin to bluerobot
   * @param  {object} options
   * @param  {string} options.accessToken  user's Auth0 access token
   * @returns {Promise<string>} The Blue Robot JWT Token
   */

  async bluerobotAuth0SignIn ({ accessToken }) {
    const response = await request.unAuthorized('/auth0-signin', {
      method: 'POST',
      body: {
        accessToken
      }
    })

    const { token } = await response.json()
    return token
  },

  /**
   * Log in to Bluerobot
   * signs into Twitter using OAuth
   * then uses the Twitter tokens to sign in to Bluerobot
   */
  async login () {
    try {
      const { accessToken, accessSecret } = await this.twitterSignin()
      const bluerobotToken = await this.bluerobotSignin({
        accessToken,
        accessSecret
      })

      // save the token in local storage
      localStorage.token = bluerobotToken
    } catch (e) {
      console.error(e)
    }
  },

  /**
   * Log in to Bluerobot
   * signs into Facebook using Firebase OAuth provider
   * then uses the Facebook access token and user ID to sign in to Bluerobot
   * @returns {Promise}
   */
  async loginWithFacebook () {
    try {
      const { accessToken, userId } = await this.facebookOAuth()
      const bluerobotToken = await this.bluerobotFbSignin({
        accessToken,
        userId
      })

      // save the token in local storage
      localStorage.token = bluerobotToken
    } catch (e) {
      console.error(e)
    }
  },

  /**
   * Log in to Bluerobot
   * signs in using Auth0
   * then uses the Auth0 access token to sign in to Bluerobot
   * @returns {Promise}
   */
  async loginWithAuth0 (accessToken) {
    try {
      const bluerobotToken = await this.bluerobotAuth0SignIn({
        accessToken
      })

      // save the token in local storage
      localStorage.token = bluerobotToken
    } catch (e) {
      console.error(e)
    }
  },

  /**
   * Log out of Bluerobot and OAuth
   */
  logout () {
    delete localStorage.token
  },

  async verifyToken () {
    const token = localStorage.token

    const requestOptions = {
      method: 'POST',
      body: {
        token
      }
    }

    const response = await request.authorized('/verify', requestOptions)

    const { valid } = await response.json()

    if (!valid) {
      this.logout()
      window.location.reload()
    }

    return valid
  },

  /**
   * Checks if user is logged in to Bluerobot
   *
   * @returns bool - the login status
   */
  async isLoggedIn () {
    if (localStorage.token) {
      return this.verifyToken()
    }

    return false
  },

  /**
   * Get the Bluerobot token for the logged in user
   *
   * @returns string - the token
   */
  get token () {
    return localStorage.token
  },

  get decodedPayload () {
    if (this.token) {
      try {
        const token = this.token
        const [, encodedPayload] = token.split('.')
        const decodedPayloadJSON = base64url.decode(encodedPayload)
        const decodedPayload = JSON.parse(decodedPayloadJSON)
        return decodedPayload
      } catch (e) {
        console.error(e)
      }
    }

    return null
  },

  get username () {
    const payload = this.decodedPayload

    if (payload) {
      return payload.name
    }

    return ''
  },

  get id () {
    const payload = this.decodedPayload

    if (payload) {
      return payload.sub
    }

    return ''
  },

  get permissions () {
    const payload = this.decodedPayload

    if (!payload) {
      return []
    }

    return payload.perm.split(' ')
  },

  get canCreateWidgets () {
    return this.permissions.some(
      either(equals('MANAGE_WIDGETS'), equals('MANAGE_OWN_WIDGETS'))
    )
  },

  get profilePicUrl () {
    const payload = this.decodedPayload

    if (payload) {
      return payload.pic
    }

    return 'http://placehold.it/100x100&text=Profile+Picture'
  }
}

export default AuthService



// WEBPACK FOOTER //
// ./src/services/auth/auth-service.js